import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { nb } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Calendar as CalendarIcon, Clock, User, Phone, Mail, Car, MapPin, Plus, Edit } from 'lucide-react';
import CreateBookingModal from './CreateBookingModal';
import EditBookingModal from './EditBookingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Setup localizer with Norwegian locale
const locales = {
  'nb': nb,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Norwegian messages for calendar
const messages = {
  allDay: 'Hele dagen',
  previous: 'Forrige',
  next: 'Neste',
  today: 'I dag',
  month: 'Måned',
  week: 'Uke',
  day: 'Dag',
  agenda: 'Agenda',
  date: 'Dato',
  time: 'Tid',
  event: 'Booking',
  noEventsInRange: 'Ingen bookinger i denne perioden',
  showMore: (total) => `+ ${total} flere`,
};

const CalendarView = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform bookings to calendar events
  const events = useMemo(() => {
    return bookings.map(booking => {
      const [hours, minutes] = booking.time_slot.split(':');
      const eventDate = new Date(booking.date);
      eventDate.setHours(parseInt(hours), parseInt(minutes), 0);

      // End time is 1 hour later
      const endDate = new Date(eventDate);
      endDate.setHours(eventDate.getHours() + 1);

      return {
        id: booking.id,
        title: `${booking.customer_name} - ${booking.vehicle_type}`,
        start: eventDate,
        end: endDate,
        resource: booking,
      };
    });
  }, [bookings]);

  const handleSelectEvent = (event) => {
    setSelectedBooking(event.resource);
    setIsModalOpen(true);
  };

  const handleSelectSlot = ({ start }) => {
    // Could be used for creating new bookings
  };

  // Custom event styling based on booking status
  const eventStyleGetter = (event) => {
    const booking = event.resource;
    let backgroundColor = '#6b7280'; // default gray
    
    switch (booking.status) {
      case 'confirmed':
        backgroundColor = '#3b82f6'; // blue
        break;
      case 'completed':
        backgroundColor = '#10b981'; // green
        break;
      case 'cancelled':
        backgroundColor = '#ef4444'; // red
        break;
      default:
        backgroundColor = '#6b7280'; // gray
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '13px',
        padding: '4px 8px',
      }
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-600/20 text-blue-400 border-blue-600/50';
      case 'completed':
        return 'bg-green-600/20 text-green-400 border-green-600/50';
      case 'cancelled':
        return 'bg-red-600/20 text-red-400 border-red-600/50';
      default:
        return 'bg-gray-600/20 text-gray-400 border-gray-600/50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster kalender...</div>
      </div>
    );
  }

  return (
    <>
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center space-x-2">
                <CalendarIcon className="w-6 h-6 text-red-400" />
                <span>Booking Kalender</span>
              </CardTitle>
              <p className="text-gray-400 text-sm mt-1">Oversikt over alle bookinger</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex space-x-2">
                <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/50">
                  Bekreftet
                </Badge>
                <Badge className="bg-green-600/20 text-green-400 border-green-600/50">
                  Fullført
                </Badge>
                <Badge className="bg-red-600/20 text-red-400 border-red-600/50">
                  Kansellert
                </Badge>
              </div>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ny Booking
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="calendar-wrapper" style={{ height: '700px' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              views={['month', 'week', 'day', 'agenda']}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              messages={messages}
              culture="nb"
              eventPropGetter={eventStyleGetter}
              popup
              tooltipAccessor={(event) => `${event.resource.customer_name} - ${event.resource.time_slot}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Booking Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center space-x-2">
              <CalendarIcon className="w-6 h-6 text-red-400" />
              <span>Booking Detaljer</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              #{selectedBooking?.booking_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center space-x-2">
                <Badge className={getStatusColor(selectedBooking.status)}>
                  {selectedBooking.status}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-red-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-400">Kunde</div>
                      <div className="text-lg font-semibold">{selectedBooking.customer_name}</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-red-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-400">Telefon</div>
                      <a href={`tel:${selectedBooking.customer_phone}`} className="text-lg hover:text-red-400">
                        {selectedBooking.customer_phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-red-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-400">Email</div>
                      <a href={`mailto:${selectedBooking.customer_email}`} className="text-lg hover:text-red-400 truncate block">
                        {selectedBooking.customer_email}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CalendarIcon className="w-5 h-5 text-red-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-400">Dato</div>
                      <div className="text-lg font-semibold">{selectedBooking.date}</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-red-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-400">Tidspunkt</div>
                      <div className="text-lg font-semibold">{selectedBooking.time_slot}</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-red-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-400">Plass</div>
                      <div className="text-lg font-semibold">#{selectedBooking.slot_number}</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Car className="w-5 h-5 text-red-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-400">Kjøretøy</div>
                      <div className="text-lg font-semibold capitalize">{selectedBooking.vehicle_type}</div>
                    </div>
                  </div>
                </div>
              </div>

              {selectedBooking.notes && (
                <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                  <div className="text-sm text-gray-400 mb-1">Notat:</div>
                  <div className="text-gray-300">{selectedBooking.notes}</div>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t border-gray-700">
                <Button
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Lukk
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsEditModalOpen(true);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Rediger Booking
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style jsx global>{`
        .calendar-wrapper .rbc-calendar {
          background: transparent;
          color: white;
        }
        
        .rbc-header {
          background: rgba(31, 41, 55, 0.5);
          padding: 12px 8px;
          font-weight: 600;
          color: #f3f4f6;
          border-bottom: 2px solid #374151;
        }
        
        .rbc-month-view, .rbc-time-view {
          background: rgba(17, 24, 39, 0.3);
          border: 1px solid #374151;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .rbc-day-bg {
          background: rgba(31, 41, 55, 0.2);
          border: 1px solid #374151;
        }
        
        .rbc-today {
          background: rgba(220, 38, 38, 0.1) !important;
        }
        
        .rbc-off-range-bg {
          background: rgba(17, 24, 39, 0.5);
        }
        
        .rbc-date-cell {
          color: #d1d5db;
          padding: 8px;
        }
        
        .rbc-off-range {
          color: #6b7280;
        }
        
        .rbc-current {
          color: #dc2626;
          font-weight: bold;
        }
        
        .rbc-toolbar {
          background: rgba(31, 41, 55, 0.5);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          border: 1px solid #374151;
        }
        
        .rbc-toolbar button {
          color: #f3f4f6;
          background: rgba(55, 65, 81, 0.8);
          border: 1px solid #4b5563;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .rbc-toolbar button:hover {
          background: #dc2626;
          border-color: #dc2626;
        }
        
        .rbc-toolbar button.rbc-active {
          background: #dc2626;
          border-color: #dc2626;
        }
        
        .rbc-toolbar-label {
          color: #f3f4f6;
          font-weight: 700;
          font-size: 18px;
        }
        
        .rbc-event {
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 13px;
        }
        
        .rbc-event:focus {
          outline: 2px solid #dc2626;
        }
        
        .rbc-agenda-view {
          background: rgba(31, 41, 55, 0.3);
          color: white;
        }
        
        .rbc-agenda-view table.rbc-agenda-table {
          border: 1px solid #374151;
        }
        
        .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
          color: #d1d5db;
          padding: 12px;
          border-color: #374151;
        }
        
        .rbc-agenda-view table.rbc-agenda-table thead > tr > th {
          background: rgba(31, 41, 55, 0.8);
          color: #f3f4f6;
          padding: 12px;
          border-color: #374151;
        }
        
        .rbc-agenda-date-cell {
          color: #f3f4f6;
        }
        
        .rbc-agenda-time-cell {
          color: #9ca3af;
        }
        
        .rbc-time-header {
          background: rgba(31, 41, 55, 0.5);
          border-color: #374151;
        }
        
        .rbc-time-content {
          border-color: #374151;
        }
        
        .rbc-timeslot-group {
          border-color: #374151;
        }
        
        .rbc-time-slot {
          color: #9ca3af;
        }
      `}</style>

      {/* Create Booking Modal */}
      <CreateBookingModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onBookingCreated={() => {
          fetchBookings(); // Refresh calendar
        }}
      />

      {/* Edit Booking Modal */}
      <EditBookingModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
        }}
        booking={selectedBooking}
        onBookingUpdated={() => {
          fetchBookings(); // Refresh calendar
          setSelectedBooking(null);
        }}
      />
    </>
  );
};

export default CalendarView;
